import { Component, OnInit, inject, DestroyRef, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Action, BaseRepublishDto, LogDto, State } from '@coin/importer/dto/util';
import { Subject } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';
import { PopupComponent } from '../../components/popup/popup.component';
import { LogsService } from '../../services/logs/logs.service';
import { ToastService } from '../../services/toast/toast.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SubHeaderComponent } from '../../components/sub-header/sub-header.component';
import { ButtonFooterComponent } from '../../components/button-footer/button-footer.component';
import { TransactionStateProgressBarComponent } from '../../components/transaction-state-progress-bar/transaction-state-progress-bar.component';
import { ImportDecisionComponent } from '../../components/import-decision/import-decision.component';
import { ChartsComponent } from '../../components/charts/charts.component';
import { ImportDetailsComponent } from '../../components/import-details/import-details.component';
import { ValidationListComponent } from '../../components/validation-list/validation-list.component';
import { KeyValuePipe } from '@angular/common';
import { FileNamePipe } from '../../pipes/file-name/file-name.pipe';
import { V2ButtonComponent } from '@coin/shared/core/ui-storybook';

@Component({
  selector: 'importer-summary',
  templateUrl: './summary.component.html',
  styleUrls: ['./summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SubHeaderComponent,
    V2ButtonComponent,
    ButtonFooterComponent,
    TransactionStateProgressBarComponent,
    ImportDecisionComponent,
    ChartsComponent,
    ImportDetailsComponent,
    ValidationListComponent,
    KeyValuePipe,
    FileNamePipe
  ]
})
export class SummaryComponent implements OnInit {
  private readonly matDialog = inject(MatDialog);
  private readonly route = inject(ActivatedRoute);
  private readonly logsService = inject(LogsService);
  private readonly router = inject(Router);
  private readonly toast = inject(ToastService);

  public data: LogDto;
  public isLoading: boolean;
  public actionEnum = Action;
  private readonly destroyRef = inject(DestroyRef);

  // eslint-disable-next-line no-restricted-syntax
  ngOnInit(): void {
    this.isLoading = true;
    this.route.params
      .pipe(
        tap(() => {
          this.isLoading = true;
        }),
        switchMap(params => this.logsService.getSingleImportLog(params.id))
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(obj => {
        this.data = obj;
        this.isLoading = false;
      });
  }

  formatErrorMessage(input: string | undefined): string {
    if (!input || typeof input !== 'string') return '';
    return input.split('jobRunId')[0].trim();
  }

  openPopup(action: Action): void {
    const popup = this.matDialog.open(PopupComponent, {
      disableClose: true,
      data: {
        log: this.data,
        action
      }
    });
    popup.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(this.popupResponse.bind(this));
  }

  private popupResponse(response?: LogDto) {
    if (response) {
      if (response.decision) {
        this.data.decision = response.decision;
        this.data.state =
          response.decision.action === Action.Reject
            ? State.Rejecting
            : response.decision.publish
              ? State.Publishing
              : State.Patching;
      }
      if (response.coinProcessingState) {
        this.data.coinProcessingState = response.coinProcessingState;
      }
    }
  }

  republishChanges(): void {
    this.logsService
      .republishChanges(this.data.id, this.data.fileType.prefix)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((republishDto: BaseRepublishDto) => {
        this.toast.success('The changes will be republished.');
        this.router.navigate(['republish_summary', republishDto.id]);
      });
  }

  goBack() {
    this.router.navigate(['']);
  }
}
